import React, { FunctionComponent, SyntheticEvent, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Card, Grid, Header } from 'semantic-ui-react';
import { ApolloError } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import { GraphQLError } from 'graphql';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { pathOr } from 'ramda';

import { useCategories } from 'helpers/useCategories';
import { ClassProp } from 'helpers/types';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import styles from 'scenes/AccountManagement/AccountManagement.styles';
import {
  POSSystem,
  USStates,
  AccountDetails,
  RequiredFieldMsg,
} from 'scenes/AccountManagement/AccountManagement.helpers';
import { UPDATE_MERCHANT_ACCOUNT_DETAILS } from 'scenes/AccountManagement/AccountManagement.queries';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormInput from 'components/FormComponents/FormInput';
import { DropDown } from 'components/FormSelect/FormSelect.helpers';

type Styles = ClassProp<ReturnType<typeof styles>>;

type UpdateResponse = {
  updateMerchantAccountDetails: {
    success: boolean;
  };
};

type Props = {
  classes: Styles;
  accountDetails: AccountDetails;
};

const getApolloError = (errors?: ApolloError | GraphQLError[]) =>
  pathOr('', ['graphQLErrors'], errors);

const renderForm = (
  classes: Styles,
  register: Function,
  handleSubmit: Function,
  { pointOfSaleSystem, category, subCategory, state }: any,
  onSelectCategory: (value: any) => void,
  handleCancel: Function,
  onSubmit: Function,
  categoryOptions: DropDown,
  subCategoryOptions: DropDown,
  isSubmitting: boolean,
) => {
  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) => handleCancel();
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid className={classes.formGrid} columns={3}>
        <Grid.Row>
          <Grid.Column>
            <FormInput
              name="title"
              type="text"
              placeholder="General Manager"
              label="Title"
              tabIndex={1}
              register={register({ required: RequiredFieldMsg })}
            />
          </Grid.Column>
          <Grid.Column>
            <FormInput
              name="address1"
              type="text"
              placeholder="1313 Mockingbird Lane"
              label="Street Address"
              tabIndex={5}
              register={register({ required: RequiredFieldMsg })}
            />
          </Grid.Column>
          <Grid.Column>
            <FormSelectList
              label="POS System"
              name="pointOfSaleSystem"
              tabIndex={10}
              dropDownOptions={POSSystem}
              defaultValue={pointOfSaleSystem}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <FormInput
              name="firstName"
              type="text"
              placeholder="Jane"
              label="First Name"
              tabIndex={2}
              register={register({ required: RequiredFieldMsg })}
            />
          </Grid.Column>
          <Grid.Column>
            <FormInput
              name="address2"
              type="text"
              placeholder="Street Address Continue"
              label="Street Address 2"
              tabIndex={6}
              register={register}
            />
          </Grid.Column>
          <Grid.Column>
            <FormSelectList
              label="Category"
              name="category"
              tabIndex={11}
              dropDownOptions={categoryOptions}
              defaultValue={category}
              onSelectChange={onSelectCategory}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <FormInput
              name="lastName"
              type="text"
              placeholder="Doe"
              label="Last Name"
              tabIndex={3}
              register={register({ required: RequiredFieldMsg })}
            />
          </Grid.Column>
          <Grid.Column>
            <FormInput
              name="city"
              type="text"
              placeholder="Austin"
              label="City"
              tabIndex={7}
              register={register({ required: RequiredFieldMsg })}
            />
          </Grid.Column>
          <Grid.Column>
            <FormSelectList
              label="Sub Category"
              name="subCategory"
              tabIndex={12}
              dropDownOptions={subCategoryOptions}
              defaultValue={subCategory}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <FormInput
              name="phone"
              type="number"
              placeholder="(512) 555-5555"
              label="Phone Number"
              tabIndex={4}
              register={register({
                required: RequiredFieldMsg,
                pattern: {
                  value: '/^d+$/g',
                  message: 'This input is number only.',
                },
                maxLength: {
                  value: 10,
                  message: 'This input exceed maxLength.',
                },
              })}
            />
          </Grid.Column>
          <Grid.Column>
            <FormSelectList
              label="State"
              name="state"
              tabIndex={8}
              dropDownOptions={USStates}
              defaultValue={state}
            />
          </Grid.Column>
          <Grid.Column />
        </Grid.Row>
        <Grid.Row>
          <Grid.Column />
          <Grid.Column>
            <FormInput
              name="postal"
              type="number"
              placeholder="Postal Code"
              label="Postal Code"
              tabIndex={9}
              register={register({
                required: RequiredFieldMsg,
                pattern: {
                  value: '/^d+$/g',
                  message: 'This input is number only.',
                },
                minLength: {
                  value: 5,
                  message: 'This input requires minimum of 5 numbers.',
                },
                maxLength: {
                  value: 5,
                  message: 'This input exceed maxLength.',
                },
              })}
            />
          </Grid.Column>
          <Grid.Column />
        </Grid.Row>
      </Grid>
      <Grid>
        <Grid.Row className={classes.formRow}>
          <button
            className={classnames(
              classes.button,
              classes.buttonSecondary,
              classes.mediumBtn,
            )}
            onClick={handleClick}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={classnames(classes.button, classes.mediumBtn)}
          >
            {isSubmitting ? '...Submitting' : 'Save'}
          </button>
        </Grid.Row>
      </Grid>
    </form>
  );
};

const AccountDetailsUpdate: FunctionComponent<Props> = ({
  classes,
  accountDetails,
}) => {
  const history = useHistory();
  const { companyName, email, merchantUuid, category } = accountDetails;

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: { ...accountDetails },
  });

  const { register, handleSubmit, getValues } = methods;
  const handleError = (error: any) =>
    toastMapping({}, 'error')(getApolloError(error) as any);

  const handleAssetApprovalResponse = (updateResponse: UpdateResponse) => {
    const success = pathOr(
      false,
      ['updateMerchantAccountDetails', 'success'],
      updateResponse,
    );
    if (success) {
      history.goBack();
    }
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    UPDATE_MERCHANT_ACCOUNT_DETAILS,
    {
      onCompleted: handleAssetApprovalResponse,
      onError: handleError,
    },
  );

  const { categoryOptions, subCategoryOptions, setCategory } = useCategories(
    category,
  );

  useEffect(() => {
    register({ name: 'pointOfSaleSystem' });
    register({ name: 'category' });
    register({ name: 'subCategory' });
    register({ name: 'state' });
  }, []);

  const onSubmit = (accountDetails: AccountDetails) => {
    runMutation({
      variables: {
        params: {
          ...accountDetails,
          email,
          merchantUuid,
          merchantName: companyName,
        },
      },
    });
  };

  return (
    <Grid className={classes.centerColumn}>
      <Grid.Column className={classes.columnWide}>
        <Header className={classes.header} as="h1">
          Update Account Info
        </Header>
        <Card className={classes.container} fluid centered>
          <FormProvider {...methods}>
            {renderForm(
              classes,
              register,
              handleSubmit,
              getValues(),
              setCategory,
              history.goBack,
              onSubmit,
              categoryOptions,
              subCategoryOptions,
              isSubmitting,
            )}
          </FormProvider>
        </Card>
      </Grid.Column>
    </Grid>
  );
};

export default withStyles(styles)(AccountDetailsUpdate);
